import { isMacPlatform } from '../utils/platform.js';

/**
 * Categories used to group shortcuts in the "Keyboard shortcuts" dialog.
 * The values double as the English source strings rendered through `<Translate>`.
 */
export enum ShortcutCategory {
  General = 'General',
  Editing = 'Editing',
  Selection = 'Selection',
  Camera = 'Camera',
}

/**
 * Sentinel token for the primary modifier key. Rendered as `⌘` on macOS and
 * `Ctrl` elsewhere, mirroring the existing history-controls behavior.
 */
export const MOD_KEY = 'Mod';

/**
 * Sentinel token for the alternate modifier key. Rendered as `⌥` (Option) on
 * macOS and `Alt` elsewhere.
 */
export const ALT_KEY = 'Alt';

export interface ShortcutDefinition {
  /** Stable identifier, unique across the registry. */
  id: string;
  /** Group the shortcut is displayed under. */
  category: ShortcutCategory;
  /** English source string describing the action; rendered through `<Translate>`. */
  description: string;
  /**
   * Key tokens that make up the combo. The `MOD_KEY` sentinel is resolved to the
   * platform modifier at render time; every other token is shown verbatim.
   */
  keys: string[];
  /** Optional alternative combo (e.g. `Backspace` as an alias for `Delete`). */
  altKeys?: string[];
  /** When true, the combo is activated by holding the key(s) rather than pressing. */
  hold?: boolean;
}

/**
 * Single source of truth for the keyboard shortcuts supported by the app.
 *
 * Both the "Keyboard shortcuts" dialog and the inline button hints read from
 * this list so they never drift from one another. Key handling still lives with
 * each feature (history, selection, camera, …); this catalog is the canonical
 * description of what those handlers bind.
 */
export const SHORTCUTS: ShortcutDefinition[] = [
  {
    id: 'show-shortcuts',
    category: ShortcutCategory.General,
    description: 'Show keyboard shortcuts',
    keys: ['?'],
  },
  {
    id: 'undo',
    category: ShortcutCategory.Editing,
    description: 'Undo',
    keys: [MOD_KEY, 'Z'],
  },
  {
    id: 'redo',
    category: ShortcutCategory.Editing,
    description: 'Redo',
    keys: [MOD_KEY, '⇧', 'Z'],
  },
  {
    id: 'delete',
    category: ShortcutCategory.Editing,
    description: 'Delete the selected element',
    keys: ['Delete'],
    altKeys: ['Backspace'],
  },
  {
    id: 'duplicate',
    category: ShortcutCategory.Editing,
    description: 'Duplicate the element (drag)',
    keys: [ALT_KEY],
  },
  {
    id: 'multi-select',
    category: ShortcutCategory.Selection,
    description: 'Add to the selection (click)',
    keys: ['⇧'],
  },
  {
    id: 'selection-box',
    category: ShortcutCategory.Selection,
    description: 'Select multiple elements (drag a box)',
    keys: ['⇧'],
  },
  {
    id: 'walk-mode',
    category: ShortcutCategory.Camera,
    description: 'Toggle walk mode',
    keys: ['P'],
  },
  {
    id: 'stop-camera-animation',
    category: ShortcutCategory.Camera,
    description: 'Stop the camera animation',
    keys: ['Esc'],
  },
];

/**
 * Resolves the platform modifier token (`⌘` on macOS, `Ctrl` elsewhere).
 */
export const getModifierKey = (isMac: boolean = isMacPlatform()): string => (isMac ? '⌘' : 'Ctrl');

/**
 * Resolves a list of key tokens to their display form for the given platform,
 * replacing the `MOD_KEY` and `ALT_KEY` sentinels with the platform keys.
 */
export const resolveKeys = (keys: string[], isMac: boolean = isMacPlatform()): string[] =>
  keys.map((key) => {
    if (key === MOD_KEY) {
      return getModifierKey(isMac);
    }
    if (key === ALT_KEY) {
      return isMac ? '⌥' : 'Alt';
    }
    return key;
  });

/**
 * Groups the registry by category, preserving definition order within each group
 * and skipping empty categories.
 */
export const getShortcutsByCategory = (): {
  category: ShortcutCategory;
  shortcuts: ShortcutDefinition[];
}[] =>
  Object.values(ShortcutCategory)
    .map((category) => ({
      category,
      shortcuts: SHORTCUTS.filter((shortcut) => shortcut.category === category),
    }))
    .filter(({ shortcuts }) => shortcuts.length > 0);
